import { LevelTheme } from './types';

/**
 * Each level maps to a real solar concept. Difficulty (speed, gap, spacing)
 * ramps up while `powerKw` teaches how real conditions change an array's output:
 * a clear noon sky out-produces dawn, haze, and storms.
 */
export const LEVELS: LevelTheme[] = [
  {
    blurb: 'At sunrise the sun is low, so irradiance, the energy hitting your panels, is weak.',
    cloud: '#FFE7D2',
    clouds: 1,
    cloudShade: '#F4C29A',
    concept: 'Irradiance',
    index: 1,
    minGap: 120,
    powerKw: 2.4,
    scrollSpeed: 85,
    sky: ['#FFD9A0', '#FF9E6D'],
    spacing: 320,
    startsAt: 0,
    title: 'FIRST LIGHT',
  },
  {
    blurb: 'Midday delivers peak sun hours, so the array hits its highest, most efficient output.',
    cloud: '#FFFFFF',
    clouds: 1,
    cloudShade: '#CFE6FA',
    concept: 'Peak Sun Hours',
    index: 2,
    minGap: 110,
    powerKw: 7.6,
    scrollSpeed: 100,
    sky: ['#8FD3FF', '#3FA9F5'],
    spacing: 305,
    startsAt: 260,
    title: 'SOLAR NOON',
  },
  {
    blurb: 'Clouds scatter sunlight, so panels still produce from this diffuse light, just less of it.',
    cloud: '#EDEFF1',
    clouds: 2,
    cloudShade: '#B7C0C7',
    concept: 'Diffuse vs. Direct',
    index: 3,
    minGap: 96,
    powerKw: 5.1,
    scrollSpeed: 116,
    sky: ['#C7D2DA', '#8FA3B0'],
    spacing: 285,
    startsAt: 600,
    title: 'DIFFUSE LIGHT',
  },
  {
    blurb: 'Storms cut output hard, which is why solar pairs with battery storage and the grid.',
    cloud: '#7C8A98',
    clouds: 2,
    cloudShade: '#4C5763',
    concept: 'Intermittency',
    decor: 'rain',
    index: 4,
    minGap: 86,
    powerKw: 2.9,
    scrollSpeed: 134,
    sky: ['#5A6B7A', '#33414E'],
    spacing: 265,
    startsAt: 1000,
    title: 'STORM FRONT',
  },
  {
    blurb: 'Dust and grime ("soiling") shade the glass, and periodic cleaning recovers lost energy.',
    cloud: '#E8D8BE',
    clouds: 3,
    cloudShade: '#C2A271',
    concept: 'Soiling Losses',
    decor: 'dust',
    index: 5,
    minGap: 78,
    powerKw: 4.3,
    scrollSpeed: 152,
    sky: ['#E3C79A', '#C99A5B'],
    spacing: 248,
    startsAt: 1450,
    title: 'DUSTY SKIES',
  },
  {
    blurb: 'Where net metering is offered, daytime surplus you export earns credits that help power the night.',
    cloud: '#27406B',
    clouds: 3,
    cloudShade: '#162647',
    concept: 'Net Metering',
    decor: 'stars',
    index: 6,
    minGap: 72,
    powerKw: 1.6,
    scrollSpeed: 170,
    sky: ['#1B2A4A', '#0A1228'],
    spacing: 236,
    startsAt: 1950,
    title: 'GRID AT NIGHT',
  },
];

export const levelForDistance = (distanceM: number): LevelTheme => {
  const eligible = LEVELS.filter((lvl) => distanceM >= lvl.startsAt);
  return eligible[eligible.length - 1] ?? LEVELS[0];
};

/** Difficulty keeps creeping up past the final authored level. */
export const effectiveScrollSpeed = (level: LevelTheme, distanceM: number): number => {
  const last = LEVELS[LEVELS.length - 1];
  if (level.index < last.index) return level.scrollSpeed;
  const overshoot = Math.max(0, distanceM - last.startsAt);
  return last.scrollSpeed + Math.min(70, overshoot * 0.02);
};
